import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import { AgGridReact } from "ag-grid-react"
import type { IRowNode, SelectionChangedEvent } from "ag-grid-community"

interface HasVariantId {
  variantId: string
}

export interface SelectedVariantHandlersContextValue<TData extends HasVariantId = HasVariantId> {
  getSelectedVariantIds: () => string[]
  getSelectedVariants: () => TData[]
  handleSelectionChanged: (event: SelectionChangedEvent) => void
  clearSelectedVariants: () => void
}

export interface SelectedVariantStateContextValue {
  selectedVariantCount: number
}

const SelectedVariantHandlersContext = createContext<
  SelectedVariantHandlersContextValue | undefined
>(undefined)

// Optional hook to check if a SelectedVariantsProvider exists above the component
export function useSelectedVariantHandlersOptional():
  | SelectedVariantHandlersContextValue
  | undefined {
  return useContext(SelectedVariantHandlersContext)
}

const SelectedVariantStateContext = createContext<SelectedVariantStateContextValue | undefined>(
  undefined,
)

export function useSelectedVariantHandlers<
  TData extends HasVariantId = HasVariantId,
>(): SelectedVariantHandlersContextValue<TData> {
  const ctx = useContext(SelectedVariantHandlersContext)
  if (!ctx) {
    throw new Error("useSelectedVariantHandlers must be used within a SelectedVariantsProvider")
  }
  return ctx as SelectedVariantHandlersContextValue<TData>
}

export function useSelectedVariantState(): SelectedVariantStateContextValue {
  const ctx = useContext(SelectedVariantStateContext)
  if (!ctx) {
    throw new Error("useSelectedVariantState must be used within a SelectedVariantsProvider")
  }
  return ctx
}

interface SelectedVariantsProviderProps<TData extends HasVariantId> {
  gridRef: RefObject<AgGridReact<TData> | null>
  /** When true, selection survives rowData filtering (e.g. search). Rows that disappear from
   *  rowData keep their "selected" status and are re-selected when they reappear. */
  persistSelection?: boolean
  children: ReactNode
}

export function SelectedVariantsProvider<TData extends HasVariantId>({
  gridRef,
  persistSelection = true,
  children,
}: SelectedVariantsProviderProps<TData>) {
  const selectedVariantIdsRef = useRef<string[]>([])
  const selectedVariantsRef = useRef<TData[]>([])
  const [selectedVariantCount, setSelectedVariantCount] = useState(0)
  // Selections keyed by AG Grid row ID (node.id) so rows sharing the same variantId
  // (e.g. size-split SKUs) each get their own entry. Only populated when `persistSelection` is true.
  const persistentSelectedRef = useRef<Map<string, TData>>(new Map())
  const isRestoringRef = useRef(false)

  const getSelectedVariantIds = useCallback(() => {
    return selectedVariantIdsRef.current
  }, [])

  const getSelectedVariants = useCallback(() => {
    return selectedVariantsRef.current
  }, [])

  // fallow-ignore-next-line complexity
  const collectSelectedFromApi = useCallback((api: AgGridReact<TData>["api"]) => {
    const selectedNodes = api.getSelectedNodes() ?? []
    const variants: TData[] = []
    const rowIds: string[] = []

    for (const node of selectedNodes) {
      const leafNodes = node.allLeafChildren || [node]
      for (const leaf of leafNodes) {
        if (leaf.data && leaf.id) {
          variants.push(leaf.data)
          rowIds.push(leaf.id)
        }
      }
    }
    return { variants, rowIds }
  }, [])

  const updateSelectionState = useCallback(
    (api: AgGridReact<TData>["api"]) => {
      const { variants, rowIds } = collectSelectedFromApi(api)

      if (persistSelection) {
        const persistent = persistentSelectedRef.current
        const gridSelectedRowIds = new Set(rowIds)
        // For rows currently visible in the grid, sync persistent state with grid state.
        // Rows not in the grid (filtered out) retain their prior persistent entries.
        api.forEachNode((node) => {
          if (!node.data || !node.id) return
          if (gridSelectedRowIds.has(node.id)) {
            persistent.set(node.id, node.data)
          } else {
            persistent.delete(node.id)
          }
        })

        const persistedVariants = [...persistent.values()]
        selectedVariantIdsRef.current = [...new Set(persistedVariants.map((v) => v.variantId))]
        selectedVariantsRef.current = persistedVariants
        setSelectedVariantCount(persistent.size)
      } else {
        selectedVariantIdsRef.current = [...new Set(variants.map((v) => v.variantId))]
        selectedVariantsRef.current = variants
        setSelectedVariantCount(variants.length)
      }
    },
    [persistSelection, collectSelectedFromApi],
  )

  // Poll for the grid api to become available, then register modelUpdated listener
  // for selection restoration. selectionChanged is handled via the context's
  // `handleSelectionChanged` which DataTableV2 wires into ag-Grid's onSelectionChanged
  // prop, so we don't need a separate listener for it here.
  // The grid is a descendant of this provider, so gridRef.current?.api is null
  // on first mount and only populated after ag-Grid finishes its own init.
  useEffect(() => {
    if (!persistSelection) return

    let cancelled = false
    let cleanup: (() => void) | undefined
    let pendingFrame: number | null = null

    const register = () => {
      if (cancelled) return
      const api = gridRef.current?.api
      if (!api) {
        requestAnimationFrame(register)
        return
      }

      const restoreSelection = () => {
        pendingFrame = null
        if (api.isDestroyed?.()) return
        const persistent = persistentSelectedRef.current
        if (persistent.size === 0) return

        const currentRowIds = new Set<string>()
        const nodesToSelect: IRowNode<TData>[] = []
        // fallow-ignore-next-line complexity
        api.forEachNode((node) => {
          if (!node.data || !node.id) return
          currentRowIds.add(node.id)
          if (persistent.has(node.id) && !node.isSelected()) {
            nodesToSelect.push(node)
          }
        })

        let pruned = false
        for (const rowId of [...persistent.keys()]) {
          if (!currentRowIds.has(rowId)) {
            persistent.delete(rowId)
            pruned = true
          }
        }

        if (nodesToSelect.length === 0) {
          if (pruned) updateSelectionState(api)
          return
        }

        isRestoringRef.current = true
        api.setNodesSelected({ nodes: nodesToSelect, newValue: true, source: "api" })
        // Force a redraw so the selection column's checkbox visual state reflects
        // the new selection — ag-Grid 35's selection column is rendered internally
        // and refreshCells alone doesn't always update it.
        api.redrawRows({ rowNodes: nodesToSelect })
        isRestoringRef.current = false
        updateSelectionState(api)
      }

      // Coalesce bursts of modelUpdated events (e.g. per-keystroke during search)
      // into a single restore pass per frame.
      const modelListener = () => {
        if (pendingFrame !== null) return
        pendingFrame = requestAnimationFrame(restoreSelection)
      }
      api.addEventListener("modelUpdated", modelListener)

      cleanup = () => {
        if (!api.isDestroyed?.()) api.removeEventListener("modelUpdated", modelListener)
        if (pendingFrame !== null) cancelAnimationFrame(pendingFrame)
      }
    }

    register()

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [gridRef, persistSelection, updateSelectionState])

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<TData>) => {
      if (isRestoringRef.current) return
      updateSelectionState(event.api)
    },
    [updateSelectionState],
  )

  const clearSelectedVariants = useCallback(() => {
    const api = gridRef.current?.api
    if (api) {
      api.deselectAll()
    }
    selectedVariantIdsRef.current = []
    selectedVariantsRef.current = []
    persistentSelectedRef.current.clear()
    setSelectedVariantCount(0)
  }, [gridRef])

  const handlersValue = useMemo<SelectedVariantHandlersContextValue<TData>>(
    () => ({
      getSelectedVariantIds,
      getSelectedVariants,
      handleSelectionChanged,
      clearSelectedVariants,
    }),
    [getSelectedVariantIds, getSelectedVariants, handleSelectionChanged, clearSelectedVariants],
  )

  const stateValue = useMemo<SelectedVariantStateContextValue>(
    () => ({ selectedVariantCount }),
    [selectedVariantCount],
  )

  return (
    <SelectedVariantHandlersContext.Provider value={handlersValue}>
      <SelectedVariantStateContext.Provider value={stateValue}>
        {children}
      </SelectedVariantStateContext.Provider>
    </SelectedVariantHandlersContext.Provider>
  )
}
